import { cn, Text } from '@youversion/design-system-components';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UploadStatus } from '@/constants/upload-workflow';
import { useSupportUrls } from '@/hooks/use-support-urls';
import { useUploadSessions } from '@/store/useUploadStateStore';

import { Draggable, useDraggable } from '../Draggable';
import { UploadCard } from './UploadCard';
import { UploadExitWarning } from './UploadExitWarning';
import { getStatusCounts, toHeaderBucketStatus } from './utils';

/**
 * A floating toast component that displays upload progress.
 * Can be dragged around the screen and collapsed/expanded.
 *
 * @returns A draggable upload toast component.
 *
 * @example
 * ```tsx
 * <UploadToast />
 * ```
 */
export function UploadToast() {
  const sessions = useUploadSessions();
  const data = Object.values(sessions);
  if (!data.length) {
    return null;
  }

  return (
    <>
      <UploadExitWarning />
      <Draggable initialPosition='bottom-end'>
        <UploadToastContent />
      </Draggable>
    </>
  );
}

type ToastLocaleKey =
  | 'upload_widget.header.error'
  | 'upload_widget.header.complete'
  | 'upload_widget.header.paused'
  | 'upload_widget.header.uploading';

/** Maps UploadStatus to locale key. */
const STATUS_TO_LOCALE_KEY: Record<UploadStatus, ToastLocaleKey> = {
  failed: 'upload_widget.header.error',
  completed: 'upload_widget.header.complete',
  paused: 'upload_widget.header.paused',
  uploading: 'upload_widget.header.uploading',
  pending: 'upload_widget.header.uploading',
};

/** The lists show each upload's progress in its own row, so the toast would repeat it. */
const LIST_ROUTE_PATTERN = /^\/(sermons|videos)$/;
/** The forms go to the list right after starting an upload, so opening the toast there only flashes. */
const UPLOAD_FORM_ROUTE_PATTERN = /^\/(sermons|videos)\/upload$/;

/**
 * Collapses on arriving at a list and expands when an upload fails anywhere else. Otherwise the
 * toast stays however the user left it.
 */
export function UploadToastContent() {
  const sessions = useUploadSessions();
  const { isDragging, onPointerDown, resetPosition, wasClick } = useDraggable();
  const pathname = usePathname()!;
  const { t } = useTranslation('common');
  const { supportUrl } = useSupportUrls();
  const isListRoute = LIST_ROUTE_PATTERN.test(pathname);
  const failedCount = Object.values(sessions).filter(session => session.status === 'failed').length;
  const [isExpanded, setIsExpanded] = useState(!isListRoute && !UPLOAD_FORM_ROUTE_PATTERN.test(pathname));
  const [previousPathname, setPreviousPathname] = useState(pathname);
  const [previousFailedCount, setPreviousFailedCount] = useState(failedCount);

  // Adjusted during render rather than in an effect, so the new state paints in the same frame.
  if (pathname !== previousPathname) {
    setPreviousPathname(pathname);
    if (isListRoute) {
      setIsExpanded(false);
    }
  }
  if (failedCount !== previousFailedCount) {
    setPreviousFailedCount(failedCount);
    if (failedCount > previousFailedCount && !isListRoute) {
      setIsExpanded(true);
    }
  }

  const { data, statusesInOrder } = useMemo(() => {
    const unorderedData = Object.values(sessions);
    const statusesInOrder = getStatusCounts(unorderedData);
    const statuses = statusesInOrder.map(statusCount => statusCount.status);

    const data = [...unorderedData].sort((a, b) => {
      const statusAIndex = statuses.indexOf(toHeaderBucketStatus(a.status));
      const statusBIndex = statuses.indexOf(toHeaderBucketStatus(b.status));
      return statusAIndex - statusBIndex;
    });

    return { data, statusesInOrder };
  }, [sessions]);

  function handlePointerUp() {
    if (wasClick()) {
      setIsExpanded(prev => !prev);
    }
  }

  // The card returns to its corner once it has finished collapsing, so the two
  // movements read as one sequence rather than happening on top of each other.
  function handlePanelTransitionEnd(event: React.TransitionEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget && event.propertyName === 'max-height' && !isExpanded) {
      resetPosition();
    }
  }

  return (
    <div className='shadow-light-4 dark:shadow-dark-4 rounded-2 w-full md:w-[420px] bg-canvas-light dark:bg-canvas-secondary-dark text-black dark:text-white'>
      <button
        aria-expanded={isExpanded}
        className={cn(
          'flex items-center justify-between rounded-2 dark:bg-surface-primary-light bg-surface-primary-dark pli-2 plb-1 w-full touch-none',
          {
            'rounded-b-0 border-b border-gray-100': isExpanded,
            'rounded-2': !isExpanded,
            'cursor-grabbing': isDragging,
            'cursor-pointer': !isDragging,
          }
        )}
        onPointerDown={onPointerDown}
        onPointerUp={handlePointerUp}
        type='button'
      >
        <div className='flex gap-1 w-11/12'>
          {statusesInOrder.map(({ count, status }) => (
            <Text className='text-white dark:text-black truncate' key={status} size='sm' variant='label'>
              {t(STATUS_TO_LOCALE_KEY[status], { count })}
            </Text>
          ))}
        </div>
        <svg
          className={`size-2 text-white dark:text-black transition-transform ${isExpanded ? '' : 'rotate-180'}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path d='M19 9l-7 7-7-7' strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} />
        </svg>
      </button>
      <div
        aria-hidden={!isExpanded}
        className={cn(
          'rounded-b-2 overflow-y-auto transition-[max-height,opacity,padding] duration-200 ease-out',
          isExpanded ? 'max-h-[500px] plb-0.5 opacity-100' : 'max-h-0 overflow-hidden py-0 opacity-0'
        )}
        inert={!isExpanded}
        onTransitionEnd={handlePanelTransitionEnd}
      >
        {data.map((uploadSession, index) => (
          <UploadCard
            data={uploadSession}
            isLast={index === data.length - 1}
            key={uploadSession.operationName}
            supportUrl={supportUrl}
          />
        ))}
      </div>
    </div>
  );
}
